import React from 'react';

// eslint-disable-next-line max-len
import RequirementContainer from 'Views/Pages/Frontend/StudyPlanWizard/CourseSelection/RequirementContainer/RequirementContainer';
import OrConditionLabel from 'Views/Pages/Frontend/StudyPlanWizard/OrConditionLabel';

import { IGeneratedPlanFeedback } from 'Queries/Hooks/StudyPlan/types';

import {
	RequirementEntity,
} from 'Models/Entities';

import { DataIndex } from 'Util/DataIndexUtils';
import { compareTwoDate } from 'Util/DateTimeUtils';
import { useWizardContext } from 'Views/Components/Wizard/WizardContext';
import { IStudyPlanWizardData } from 'Views/Pages/Frontend/StudyPlanWizard/StudyPlanWizard';
import { CourseSelectionItemProps }
	from 'Views/Pages/Frontend/StudyPlanWizard/CourseSelection/RequirementContainer/CourseSelectionItem';
import Spinner from 'Views/Components/Spinner/Spinner';
import { useRequirementIndex } from 'Views/Pages/Frontend/StudyPlanWizard/RequirementIndexContext';

interface IRequirementListProps {
	currentComponent: RequirementEntity | undefined;
	requisiteWarningsByCourseId: DataIndex<IGeneratedPlanFeedback['requisiteWarnings']>;
	updatePlanSelections: CourseSelectionItemProps['updatePlanSelections']
}

const RequirementList = ({
	currentComponent,
	requisiteWarningsByCourseId,
	updatePlanSelections,
}: IRequirementListProps) => {
	const { data } = useWizardContext<IStudyPlanWizardData>();

	const {
		requirementTree,
	} = useRequirementIndex();

	const dataIsReady = (
		requirementTree
		&& currentComponent?.id
		&& requirementTree[currentComponent.id]
		&& data?.fetchPlanRequestState === 'done'
	);

	if (!dataIsReady) {
		return <Spinner />;
	}

	const currentRequirementNode = requirementTree[currentComponent.id];
	/*
		If the program structure Component was an OR condition
		it means that a requirement was selected for it during step 1 of the wizard
		to further confirm this, we check that it only has a single child

		if this is true, then we select that child requirement to be the requirement
		who's contents is listed on the page.
	*/
	const topLevelRequirementWasSelected = (
		currentRequirementNode.requirement.condition === 'OR'
		&& currentRequirementNode.childNodes.length === 1
	);

	const rootPageNode = topLevelRequirementWasSelected
		? currentRequirementNode.childNodes[0]
		: currentRequirementNode;

	return (
		<>
			<OrConditionLabel
				key={`${rootPageNode.requirement.id}-condition-label`}
				className="root-level-condition"
				childRequirements={rootPageNode.childNodes.map(node => node.requirement)}
				visible={rootPageNode.requirement.condition === 'OR'}
			/>
			{
				rootPageNode.childNodes
					.sort(
						(a, b) => compareTwoDate(
							new Date(a.requirement.created),
							new Date(b.requirement.created),
						),
					)
					.map(childNode => (
						<RequirementContainer
							key={childNode.requirement.id}
							requirementSubtree={childNode}
							requisiteWarningsByCourseId={requisiteWarningsByCourseId}
							updatePlanSelections={updatePlanSelections}
						/>
					))
			}
		</>
	);
};

export default RequirementList;
